/**
 * Main App Component
 * This file serves as the root component of the Recomp Fitness application.
 * It handles routing, authentication, global settings, and chat functionality.
 */

import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./App.css";

// Route Components
import PublicRoutes from "./routes/PublicRoutes";
import { ToastContainer } from "react-toastify";
import { ChatProvider } from "./context/ChatContext";
import { setAuthToken } from "./api/api";
import "react-phone-input-2/lib/style.css";

// Redux and API imports
import { setGlobalSettings } from "./redux/slices/globalSettingsSlice";
import { getGlobalSettingsData } from "./services/clientWebsite";

function App() {
  // Redux state and dispatch hooks
  const token = useSelector((state) => state.auth.token);
  const role = useSelector((state) => state.auth.role);
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  /**
   * Disables all console logging in production environment
   * This helps in preventing sensitive information leaks and reduces browser memory usage
   * Console logs will still work in development environment for debugging purposes
   */
  const disableConsoleInProduction = () => {
    if (process.env.NODE_ENV === "production") {
      console.log = () => { };
      console.error = () => { };
      console.debug = () => { };
      console.warn = () => { };
      console.info = () => { };
    }
  };


  // Initialize console log handling for production environment
  useEffect(() => {
    disableConsoleInProduction();
  }, []);

  // Set authentication token whenever it changes
  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  // Fetch global settings on component mount
  useEffect(() => {
    fetchGlobalSettingsData();
  }, []);

  /**
   * Fetches global application settings from the server
   * These settings are stored in Redux state and used throughout the application
   * @returns {Promise<void>}
   */
  const fetchGlobalSettingsData = async () => {
    try {
      const resp = await getGlobalSettingsData();
      if (resp.success) {
        dispatch(setGlobalSettings(resp.data));
      }
    } catch (error) {
      console.error("Error fetching global settings:", error);
    }
  };

  return (
    <div className="App">
      <Router>
        {/* Toast notifications container for displaying alerts and messages */}
        <ToastContainer />
        <Routes>
          {/* 
            Main application routes
            The wildcard path "/*" catches all routes and passes them to PublicRoutes component
            ChatProvider wraps the routes to provide chat functionality throughout the app
          */}
          <Route
            path="/*"
            element={
              <ChatProvider user={user}>
                <PublicRoutes />
              </ChatProvider>
            }
          />
        </Routes>
      </Router>
    </div>
  );
}

export default App;
